import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { incentiveAPI, leadDashboardsAPI, incentiveCenterAPI, adminAnalyticsAPI, myAnalyticsAPI } from '../lib/api';
import SimulatorIncentives from './SimulatorIncentives';
import { MyActionPlanCard } from '../components/MyActionPlanCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select as PeriodSelect, SelectContent as PSC,
  SelectItem as PSI, SelectTrigger as PST, SelectValue as PSV,
} from '../components/ui/select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, LineChart as ReLineChart, Line,
} from 'recharts';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
// === ANALYTICS+PERIOD v1 ===
import {
  Award, Target, TrendingUp, Users, IndianRupee, Save, Loader2, Trophy, Flame, CheckCircle2, BarChart3,
  DollarSign, PiggyBank, LineChart, Percent, Lock, KeyRound, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const StatTile = ({ label, value, sub, icon: Icon, tint = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    rose: 'bg-rose-50 text-rose-700',
  };
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg ${tones[tint]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function IncentiveCenter() {
  const { user, isAdmin } = useAuth();
  const designation = user?.designation;
  const canViewTeam = isAdmin() || designation === 'Admission Manager' || designation === 'Team Lead';
  const canEditConfig = isAdmin();

  // Password gate (this page is sensitive — payout data) — applies to admin only
  const adminUser = isAdmin();
  const [unlocked, setUnlocked] = useState(() => !isAdmin() || sessionStorage.getItem('incentive_unlocked') === '1');
  const [gatePwd, setGatePwd] = useState('');
  const [gateError, setGateError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [hasCustomPwd, setHasCustomPwd] = useState(true);

  // Change password dialog (admin)
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      incentiveCenterAPI.status().then((r) => setHasCustomPwd(!!r.data.has_custom_password)).catch(() => {});
    }
  }, []);

  const doUnlock = async (e) => {
    e && e.preventDefault();
    setGateError('');
    if (!gatePwd) { setGateError('Please enter the password'); return; }
    setUnlocking(true);
    try {
      await incentiveCenterAPI.unlock(gatePwd);
      sessionStorage.setItem('incentive_unlocked', '1');
      setUnlocked(true);
      setGatePwd('');
    } catch (err) {
      setGateError(err.response?.data?.detail || 'Incorrect password');
    } finally {
      setUnlocking(false);
    }
  };

  const doLock = () => {
    sessionStorage.removeItem('incentive_unlocked');
    setUnlocked(false);
  };

  const doChangePassword = async () => {
    if (pwdForm.new_password.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await incentiveCenterAPI.setPassword({
        current_password: hasCustomPwd ? pwdForm.current_password : undefined,
        new_password: pwdForm.new_password,
      });
      toast.success('Incentive Center password updated');
      setPwdDialogOpen(false);
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
      setHasCustomPwd(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setPwdSaving(false);
    }
  };

  const [config, setConfig] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [team, setTeam] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // === ANALYTICS+PERIOD v1 ===
  const [period, setPeriod] = useState('current'); // 'current' | 'last' | 'all' | 'YYYY-MM'
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  // === MY-PERF-INC v1 ===
  const [myAnalytics, setMyAnalytics] = useState(null);
  const periodOptions = React.useMemo(() => {
    const opts = [
      { value: 'current', label: 'This Month' },
      { value: 'last',    label: 'Last Month' },
      { value: 'all',     label: 'All Months' },
    ];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const lbl = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      opts.push({ value: ym, label: lbl });
    }
    return opts;
  }, []);

  const [form, setForm] = useState({
    base_incentive: 500, monthly_target: 10, complementary_incentive: 10000,
    additional_incentive: 750, currency: 'INR',
  });

  useEffect(() => {
    if (!unlocked) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const cfg = await incentiveAPI.getConfig();
        setConfig(cfg.data.config);
        setForm({
          base_incentive: cfg.data.config.base_incentive,
          monthly_target: cfg.data.config.monthly_target,
          complementary_incentive: cfg.data.config.complementary_incentive,
          additional_incentive: cfg.data.config.additional_incentive,
          currency: cfg.data.config.currency || 'INR',
        });
      } catch (e) { /* silent */ }
      if (!isAdmin()) {
        try {
          const me = await incentiveAPI.mine({ period });
          setMyStats(me.data);
        } catch (e) { /* silent */ }
      }
      if (canViewTeam) {
        try {
          const t = await incentiveAPI.team({ period });
          setTeam(t.data);
        } catch (e) { /* silent */ }
      }
      if (isAdmin() || designation === 'Admission Manager') {
        try {
          const ad = await leadDashboardsAPI.admin({ period });
          setAdmin(ad.data);
        } catch (e) { /* silent */ }
        try {
          const ins = await leadDashboardsAPI.adminInsights({ period });
          setInsights(ins.data);
        } catch (e) { /* silent */ }
      }
      setLoading(false);
    })();
  }, [canViewTeam, isAdmin, designation, unlocked, period]);
  // === ANALYTICS+PERIOD v1 ===
  // === PERIOD-WIRE-ALL v1 ===
  useEffect(() => {
    if (!unlocked) return;
    if (!isAdmin() && designation !== 'Admission Manager' && designation !== 'Team Lead') return;
    setLoadingAnalytics(true);
    adminAnalyticsAPI.incentives({ months: 6, period })
      .then((r) => setAnalytics(r.data))
      .catch(() => { /* silent */ })
      .finally(() => setLoadingAnalytics(false));
  }, [unlocked, isAdmin, designation, period]);
  // === MY-PERF-INC v1 ===
  useEffect(() => {
    if (!unlocked) return;
    if (isAdmin() || designation === 'Admission Manager' || designation === 'Team Lead') return;
    myAnalyticsAPI.leads({ months: 6 })
      .then((r) => setMyAnalytics(r.data))
      .catch(() => { /* silent */ });
  }, [unlocked, isAdmin, designation]);


  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await incentiveAPI.setConfig({
        base_incentive: Number(form.base_incentive),
        monthly_target: Number(form.monthly_target),
        complementary_incentive: Number(form.complementary_incentive),
        additional_incentive: Number(form.additional_incentive),
        currency: form.currency,
      });
      setConfig(data.config);
      toast.success('Incentive config saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-16">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#0066CC] to-[#003d7a] text-white flex items-center justify-center mb-3 shadow-lg">
                  <Lock className="h-7 w-7" />
                </div>
                <h1 className="text-xl font-semibold text-slate-900">Incentive Center Locked</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Payout details are sensitive. Enter the secondary password to continue.
                </p>
                {!hasCustomPwd && isAdmin() && (
                  <p className="text-[11px] text-amber-600 mt-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Default password is in use. Change it from the dialog below after unlocking.
                  </p>
                )}
              </div>
              <form onSubmit={doUnlock} className="space-y-3" data-testid="incentive-gate-form">
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={gatePwd}
                    onChange={(e) => setGatePwd(e.target.value)}
                    autoFocus
                    placeholder="Enter Incentive Center password"
                    data-testid="incentive-gate-password"
                  />
                  {gateError && <p className="text-xs text-rose-600 mt-1" data-testid="incentive-gate-error">{gateError}</p>}
                </div>
                <Button
                  type="submit"
                  disabled={unlocking}
                  className="w-full bg-[#0066CC] hover:bg-[#0052A3]"
                  data-testid="incentive-gate-submit"
                >
                  {unlocking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}
                  Unlock
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#0066CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Award className="h-6 w-6 text-[#0066CC]" /> Incentive Center
              {adminUser && (
                <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1 ml-2">
                  <ShieldCheck className="h-3 w-3" /> Unlocked
                </Badge>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Live tracking of monthly admissions, targets and payouts.</p>
          </div>
          <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 mr-2" data-testid="incentive-period-wrap">
                <CalendarIcon className="h-4 w-4 text-slate-500" />
                <PeriodSelect value={period} onValueChange={setPeriod}>
                  <PST className="h-9 w-[170px]" data-testid="incentive-period-select">
                    <PSV />
                  </PST>
                  <PSC>
                    {periodOptions.map((o) => (
                      <PSI key={o.value} value={o.value} data-testid={`period-${o.value}`}>{o.label}</PSI>
                    ))}
                  </PSC>
                </PeriodSelect>
              </div>
            {canEditConfig && (
              <Button variant="outline" size="sm" onClick={() => setPwdDialogOpen(true)} data-testid="change-incentive-pwd-btn">
                <KeyRound className="h-4 w-4 mr-1" /> {hasCustomPwd ? 'Change Password' : 'Set Password'}
              </Button>
            )}
            {adminUser && (
              <Button variant="outline" size="sm" onClick={doLock} data-testid="lock-incentive-btn">
                <Lock className="h-4 w-4 mr-1" /> Lock
              </Button>
            )}
          </div>
        </div>

        {/* Admin Overview */}
        {admin && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label="Total Leads" value={admin.totals.total_leads} icon={Flame} tint="blue" />
            <StatTile label="Admissions Confirmed" value={admin.totals.admissions_confirmed} sub={`Conversion: ${admin.totals.conversion_rate}%`} icon={CheckCircle2} tint="green" />
            <StatTile label="Potential (In Progress)" value={admin.totals.potential_admissions} icon={TrendingUp} tint="amber" />
            <StatTile label="Incentive Payout" value={inr(admin.totals.incentive_payout)} sub={`Base: ${inr((admin.config?.base_incentive || 0))} · Target: ${admin.config?.monthly_target || 0}`} icon={IndianRupee} tint="purple" />
          </div>
        )}

        {/* Admin Revenue & Insights */}
        {insights && (
          <>
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" /> Revenue & Insights
                  <Badge variant="outline" className="ml-auto">{insights.year}-{String(insights.month).padStart(2, '0')}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="Revenue Generated" value={inr(insights.core_metrics.revenue_generated)} sub={`${insights.core_metrics.total_admissions} admissions`} icon={DollarSign} tint="green" />
                <StatTile label="Potential Revenue" value={inr(insights.core_metrics.potential_revenue)} sub={`${insights.core_metrics.potential_admissions} in progress`} icon={TrendingUp} tint="amber" />
                <StatTile label="Scholarship Offered" value={inr(insights.core_metrics.scholarship_offered)} sub="From admissions this month" icon={PiggyBank} tint="blue" />
                <StatTile
                  label="Profitability"
                  value={inr(insights.core_metrics.profitability)}
                  sub={`Margin ${insights.core_metrics.profit_margin_pct}% · Incentive ${inr(insights.core_metrics.total_incentive_payout)}`}
                  icon={LineChart}
                  tint={insights.core_metrics.profitability >= 0 ? 'purple' : 'rose'}
                />
              </CardContent>
            </Card>

            {(insights.revenue_by_course.length > 0 || insights.revenue_by_college.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.revenue_by_course.length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Revenue by Course</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {(() => {
                        const maxV = insights.revenue_by_course[0]?.amount || 1;
                        return insights.revenue_by_course.map((r) => (
                          <div key={r.label} className="flex items-center gap-3 text-sm">
                            <span className="w-44 truncate">{r.label}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div className="bg-emerald-500 h-2" style={{ width: `${(r.amount / maxV) * 100}%` }} />
                            </div>
                            <span className="w-24 text-right font-medium">{inr(r.amount)}</span>
                          </div>
                        ));
                      })()}
                    </CardContent>
                  </Card>
                )}
                {insights.revenue_by_college.length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Revenue by College</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {(() => {
                        const maxV = insights.revenue_by_college[0]?.amount || 1;
                        return insights.revenue_by_college.map((r) => (
                          <div key={r.label} className="flex items-center gap-3 text-sm">
                            <span className="w-44 truncate">{r.label}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div className="bg-[#0066CC] h-2" style={{ width: `${(r.amount / maxV) * 100}%` }} />
                            </div>
                            <span className="w-24 text-right font-medium">{inr(r.amount)}</span>
                          </div>
                        ));
                      })()}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {insights.top_counselors.length > 0 && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Top Performing Counselors</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Counselor</TableHead>
                        <TableHead>Admissions</TableHead>
                        <TableHead>Revenue</TableHead>
                        <TableHead>Incentive</TableHead>
                        <TableHead className="text-right">Share of Admissions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {insights.top_counselors.map((r) => (
                        <TableRow key={r.counselor_id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{r.name}<span className="block text-xs text-slate-500">{r.designation}</span></TableCell>
                          <TableCell>{r.admissions}{r.target_hit && <Badge className="ml-2 bg-emerald-100 text-emerald-700">Target hit</Badge>}</TableCell>
                          <TableCell className="font-semibold text-emerald-700">{inr(r.revenue)}</TableCell>
                          <TableCell>{inr(r.incentive)}</TableCell>
                          <TableCell className="text-right">{r.conversion_contribution}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* === MY-PERF-INC v1 === Personal performance card (counselor view) */}
        {!isAdmin() && designation !== 'Admission Manager' && designation !== 'Team Lead' && myAnalytics && (
          <Card className="border-0 shadow-sm" data-testid="my-perf-card-inc">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-600" /> My Performance
                {myAnalytics.kpis.my_rank ? (
                  <Badge className="ml-auto bg-emerald-50 border-emerald-200 text-emerald-700">
                    Rank #{myAnalytics.kpis.my_rank} of {myAnalytics.kpis.total_in_cohort}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="ml-auto">Last 6 months</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="My Leads"      value={myAnalytics.kpis.total_leads}                                  icon={Users}    tint="blue" />
                <StatTile label="Confirmed"     value={myAnalytics.kpis.confirmed_total}                              icon={Trophy}   tint="green" />
                <StatTile label="Conversion"    value={`${myAnalytics.kpis.conversion_pct}%`}                          icon={Target}   tint="purple" />
                <StatTile label="Avg Time"      value={`${myAnalytics.kpis.avg_days_to_conversion} d`}                 icon={Clock}    tint="amber" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="my-funnel-chart-inc">
                  <p className="text-xs font-semibold text-slate-600 mb-2 px-1">My Conversion Funnel</p>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={myAnalytics.funnel} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ReTooltip />
                      <Bar dataKey="count" fill="#10b981" name="Leads" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="my-trend-chart-inc">
                  <p className="text-xs font-semibold text-slate-600 mb-2 px-1">My Monthly Trend</p>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={myAnalytics.monthly_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ReTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="new_leads" fill="#3b82f6" name="New Leads" />
                      <Bar dataKey="confirmed" fill="#10b981" name="Confirmed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* === ACTION-PLAN v1 === */}
        {!isAdmin() && designation !== 'Admission Manager' && designation !== 'Team Lead' && (
          <MyActionPlanCard testIdSuffix="-inc" />
        )}

        {/* === ANALYTICS+PERIOD v1 === Advanced Analytics (admin / team lead) */}
        {analytics && (isAdmin() || designation === 'Admission Manager' || designation === 'Team Lead') && (
          <Card className="border-0 shadow-sm" data-testid="incentive-analytics-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" /> Advanced Analytics
                <Badge variant="outline" className="ml-auto bg-indigo-50 border-indigo-200 text-indigo-700">
                  {analytics?.period_label || 'Selected period'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="YTD Payout"  value={inr(analytics.kpis.ytd_payout)} icon={IndianRupee} tint="purple" />
                <StatTile label="YTD Admissions" value={analytics.kpis.ytd_admissions} sub={`Avg ${analytics.kpis.avg_admissions_per_counselor}/counselor`} icon={Trophy} tint="green" />
                <StatTile label="Target Hit Rate" value={`${analytics.kpis.target_hit_rate_pct}%`} icon={Target} tint="amber" />
                <StatTile label="Active Counselors" value={analytics.kpis.active_counselors} icon={Users} tint="blue" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="incentive-trend-chart">
                  <p className="text-xs font-semibold text-slate-600 mb-2 px-1">Monthly Payout Breakdown</p>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analytics.monthly_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <ReTooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="base"   stackId="a" fill="#3b82f6" name="Base" />
                      <Bar dataKey="bonus"  stackId="a" fill="#10b981" name="Bonus" />
                      <Bar dataKey="extras" stackId="a" fill="#f59e0b" name="Extras" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="incentive-adm-chart">
                  <p className="text-xs font-semibold text-slate-600 mb-2 px-1">Monthly Admissions</p>
                  <ResponsiveContainer width="100%" height={240}>
                    <ReLineChart data={analytics.monthly_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ReTooltip />
                      <Line type="monotone" dataKey="admissions" stroke="#0066CC" strokeWidth={2} dot={{ r: 3 }} />
                    </ReLineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {analytics.top_counselors?.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200" data-testid="incentive-top-counselors">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                      <Trophy className="h-3.5 w-3.5 text-amber-500" /> Top 5 Counselors — {analytics?.period_label || "Selected period"}
                    </p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Counselor</TableHead>
                        <TableHead>Admissions</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead className="text-right">Incentive</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.top_counselors.map((c, idx) => (
                        <TableRow key={c.counselor_id}>
                          <TableCell className="font-semibold text-slate-500">#{idx + 1}</TableCell>
                          <TableCell className="font-medium">{c.name}<span className="block text-xs text-slate-400">{c.designation}</span></TableCell>
                          <TableCell>{c.admissions}{c.target_hit && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">Target hit</Badge>}</TableCell>
                          <TableCell className="text-slate-500">{c.target}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 w-32">
                              <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                <div className={`${c.target_hit ? 'bg-emerald-500' : 'bg-blue-500'} h-1.5 rounded-full`} style={{ width: `${Math.min(c.target_progress_pct, 100)}%` }} />
                              </div>
                              <span className="text-[11px] text-slate-500">{c.target_progress_pct}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700">{inr(c.total_incentive)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Personal Incentive */}
        {myStats && !isAdmin() && (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-br from-[#0066CC] to-[#003d7a] text-white p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide opacity-80">Your incentive this month</p>
                  <p className="text-4xl font-bold mt-1">{inr(myStats.total_incentive)}</p>
                  <p className="text-sm opacity-90 mt-1">{myStats.confirmed_count} of {myStats.monthly_target} admissions · {myStats.target_progress_pct}%</p>
                </div>
                <Trophy className="h-12 w-12 opacity-80" />
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 mt-4 overflow-hidden">
                <div className="bg-white h-2 rounded-full" style={{ width: `${Math.min(myStats.target_progress_pct, 100)}%` }} />
              </div>
            </div>
            <CardContent className="p-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 uppercase">Base</p>
                <p className="text-xl font-semibold">{inr(myStats.base_total)}</p>
                <p className="text-xs text-slate-500">{inr(myStats.base_incentive)}/adm</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Target Bonus</p>
                <p className={`text-xl font-semibold ${myStats.target_hit ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {myStats.target_hit ? inr(myStats.complementary_earned) : 'Locked'}
                </p>
                <p className="text-xs text-slate-500">at {myStats.monthly_target} admissions</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Extra Admissions</p>
                <p className="text-xl font-semibold">{inr(myStats.extra_total)}</p>
                <p className="text-xs text-slate-500">{myStats.extra_admissions} × {inr(myStats.additional_incentive)}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team leaderboard */}
        {team && canViewTeam && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Team Leaderboard</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">{team.rows.length} counselor{team.rows.length === 1 ? '' : 's'} · team payout {inr(team.team_total)}</p>
              </div>
              <Badge variant="outline">{team.year}-{String(team.month).padStart(2, '0')}</Badge>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Admissions</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Base</TableHead>
                    <TableHead>Target Bonus</TableHead>
                    <TableHead>Extras</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.rows.map((r) => (
                    <TableRow key={r.counselor?.id} className="hover:bg-slate-50" data-testid={`team-row-${r.counselor?.id}`}>
                      <TableCell className="font-medium">{r.counselor?.name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.counselor?.designation}</TableCell>
                      <TableCell>{r.confirmed_count}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 w-36">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                            <div className={`${r.target_hit ? 'bg-emerald-500' : 'bg-blue-500'} h-1.5 rounded-full`} style={{ width: `${Math.min(r.target_progress_pct, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-500">{r.target_progress_pct}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{inr(r.base_total)}</TableCell>
                      <TableCell>{r.target_hit ? inr(r.complementary_earned) : <span className="text-slate-300">Locked</span>}</TableCell>
                      <TableCell>{inr(r.extra_total)}</TableCell>
                      <TableCell className="text-right font-semibold">{inr(r.total_incentive)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Admin configuration */}
        {canEditConfig && config && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4" /> Incentive Configuration</CardTitle>
              <p className="text-xs text-slate-500">Applies to all counselors. Changes take effect immediately.</p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Base per admission (₹)</Label>
                <Input type="number" value={form.base_incentive} onChange={(e) => setForm({ ...form, base_incentive: e.target.value })} data-testid="cfg-base" />
              </div>
              <div>
                <Label>Monthly Target (admissions)</Label>
                <Input type="number" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} data-testid="cfg-target" />
              </div>
              <div>
                <Label>Target Bonus (₹)</Label>
                <Input type="number" value={form.complementary_incentive} onChange={(e) => setForm({ ...form, complementary_incentive: e.target.value })} data-testid="cfg-bonus" />
              </div>
              <div>
                <Label>Extra per admission (₹)</Label>
                <Input type="number" value={form.additional_incentive} onChange={(e) => setForm({ ...form, additional_incentive: e.target.value })} data-testid="cfg-extra" />
              </div>
              <div className="md:col-span-4 flex justify-end">
                <Button onClick={saveConfig} disabled={saving} data-testid="cfg-save" className="bg-[#0066CC] hover:bg-[#0052A3]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Choice Entry Simulator Incentive Module */}
        <SimulatorIncentives />

        {/* Admin: Top course demand */}
        {admin?.course_demand?.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Top courses in demand</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {admin.course_demand.map((c) => {
                  const max = admin.course_demand[0].count || 1;
                  const pct = (c.count / max) * 100;
                  return (
                    <div key={c.course} className="flex items-center gap-3 text-sm">
                      <span className="w-48 truncate">{c.course}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="bg-[#0066CC] h-2" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right font-medium">{c.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Change Password Dialog (admin) */}
      <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> {hasCustomPwd ? 'Change' : 'Set'} Incentive Center Password</DialogTitle>
            <DialogDescription>
              {hasCustomPwd
                ? 'Updates the password required to open this page. Existing unlocks remain valid until the user locks or refreshes the session.'
                : 'You are using the default password. Set your own to restrict access.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {hasCustomPwd && (
              <div>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={pwdForm.current_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                  data-testid="cur-pwd"
                />
              </div>
            )}
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                data-testid="new-pwd"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                data-testid="confirm-pwd"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
            <Button onClick={doChangePassword} disabled={pwdSaving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-incentive-pwd">
              {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
